import { HttpInterceptorFn } from '@angular/common/http';
import { MissingApiKeyError } from './api-error';
import { inject } from '@angular/core';
import { from, switchMap, throwError } from 'rxjs';
import { TokenStoreService } from '../auth/token-store.service';
import { LocalStorageManagerService } from '../storage/local-storage-manager.service';
import { ApiKeyCacheService } from './api-key-cache.service';
import { HeaderConstant } from './header-constant';
import { API_TYPE, TELEMETRY_REQUEST, resolveApiType, resolveEncryptedApiKey } from './api-type';
import { AppConfigStore } from '../app-config/app-config.store';
import { environment } from '../../../environments/environment';
import { NetworkConstant } from './network-constant';

/**
 * Attaches auth/identity headers based on URL-pattern classification —
 * Angular functional-interceptor port of Consumer's network/RequestInterceptor.ts.
 * Same four request classes as Consumer:
 *   - SSO (guest-token issuance): SSO token + identity headers
 *   - CDN (static appconfigs.json/feature-toggle host): NO auth headers at all
 *     (sending them triggers a CORS preflight the static host rejects — see
 *     Consumer's comment in RequestInterceptor.ts, preserved here verbatim)
 *   - GuestToken (login/eligibility/registration/OTP/trial APIs): guest token + identity headers
 *   - AccessToken (everything else, once logged in): bearer access token + identity headers
 *
 * The resolved {@link ApiType} is stamped onto the request's HttpContext so
 * `response.interceptor.ts` can decide whether a 401 warrants a refresh
 * attempt (only for `AccessToken` requests, matching Consumer).
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Only classify/decorate calls to our own API host — leave third-party
  // requests (e.g. i18n JSON from /i18n/, CDN webview URLs) untouched.
  if (
    !req.url.startsWith(environment.envDomainUrl) &&
    !req.url.startsWith(environment.cdnDomainUrl)
  ) {
    return next(req);
  }

  const tokenStore = inject(TokenStoreService);
  const localStorageManager = inject(LocalStorageManagerService);
  const apiKeyCache = inject(ApiKeyCacheService);
  const appConfigStore = inject(AppConfigStore);

  const accessToken = tokenStore.getAccessToken();
  const guestToken = localStorageManager.getGuestToken();
  const apiType = resolveApiType(req.url, !!accessToken);

  if (apiType === 'CDN' || apiType === 'None') {
    // 'CDN': static host rejects auth headers (see class doc comment above).
    // 'None': no URL pattern matched and there's no access token yet — Consumer's
    // RequestInterceptor sends these requests through with no auth/identity
    // headers attached rather than guessing at partial credentials.
    return next(req.clone({ context: req.context.set(API_TYPE, apiType) }));
  }

  // Functional logs use `analyticsAPIKey`; everything else `clientApiKey`. Wait on
  // AppConfigStore (de-duped, so this is a no-op once loaded) rather than trusting that
  // whatever triggered this request already ran behind `appConfigReadyGuard` — the
  // startup silent-refresh path, for one, doesn't. `waitReady()` is bounded so a hung
  // CMS fetch can't stall every request forever — it falls through to whatever key is
  // (or isn't) already cached once the bound elapses.
  return from(appConfigStore.waitReady()).pipe(
    switchMap(() => {
      const encryptedApiKey = resolveEncryptedApiKey(req.url, {
        clientApiKey: localStorageManager.getClientApiKey() ?? '',
        analyticsAPIKey: localStorageManager.getAnalyticsApiKey() ?? '',
      });
      if (!encryptedApiKey) {
        return throwError(() => new MissingApiKeyError(`request: ${req.url}`));
      }
      return apiKeyCache.getDecryptedKey(encryptedApiKey);
    }),
    switchMap((apiKey) => {
      const headers: Record<string, string> = {
        [HeaderConstant.HEADER_CONTENT_TYPE]: HeaderConstant.CONTENT_TYPE_JSON,
        [HeaderConstant.HEADER_USER_X_API_KEY]: apiKey,
      };

      if (apiType === 'SSO') {
        const ssoToken = localStorageManager.getSsoToken();
        if (ssoToken) headers[HeaderConstant.HEADER_AUTHORIZATION] = ssoToken;
        Object.assign(headers, identityHeaders(localStorageManager, req.url));
      } else if (apiType === 'GuestToken') {
        if (guestToken) headers[HeaderConstant.HEADER_AUTHORIZATION] = guestToken;
        Object.assign(headers, identityHeaders(localStorageManager, req.url));
      } else if (apiType === 'AccessToken') {
        if (accessToken) headers[HeaderConstant.HEADER_AUTHORIZATION] = accessToken;
        // Telemetry batch uploads are session-keyed and must not include user PII identity headers
        if (!req.context.get(TELEMETRY_REQUEST)) {
          Object.assign(headers, identityHeaders(localStorageManager, req.url));
        }
      }

      return next(
        req.clone({
          setHeaders: headers,
          context: req.context.set(API_TYPE, apiType),
        }),
      );
    }),
  );
};

function identityHeaders(
  localStorageManager: LocalStorageManagerService,
  requestUrl: string,
): Record<string, string> {
  const { subscriberId, mobileNo, uniqueId } = localStorageManager.getGuestRequestHeaders();
  const reqSubId = localStorageManager.getRequestSubscriberId() || subscriberId;
  const isUserLog = requestUrl.includes(NetworkConstant.USER_LOG);

  return {
    // Dual-schema compatibility:
    // - Legacy BE expects 'subscriberId' header across auth/SSO/guest requests.
    // - New BE accepts 'encryptedsubscriberId' header on user-log endpoints.
    ...(reqSubId
      ? {
          [HeaderConstant.HEADER_SUBSCRIBER_ID]: reqSubId,
          ...(isUserLog ? { [HeaderConstant.HEADER_ENCRYPTED_SUBSCRIBER_ID]: reqSubId } : {}),
        }
      : {}),
    ...(mobileNo ? { [HeaderConstant.HEADER_MOBILE_NUMBER]: mobileNo } : {}),
    ...(uniqueId ? { [HeaderConstant.HEADER_UNIQUE_ID]: uniqueId } : {}),
  };
}
